import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getItemUomConversions } from '../api/itemUom';
import {
  normalizeUomConversionList,
  buildConversionGraph,
  resolveConversion,
  resolveUomCodeById,
} from '../utils/uomConversionEntity';

const UNRESOLVED_SUFFIX = ' — no conversion path';

/**
 * Allowed line UOMs for an item = base UOM + every UOM whose conversion-graph
 * path reaches base (store.md §4.0). Restricting the picker to these is what
 * stops an unpostable UOM being chosen at line-entry instead of failing later
 * at post-to-stock, where the GRN can no longer be edited.
 *
 * @param baseUomByItem  { [itemId]: baseUomId } — taken from the line's own
 *   selected item rather than the shared catalog, which only caches the first
 *   page of items and so misses most of them.
 *
 * Rules are fetched once per distinct item id and cached for the hook's life,
 * so a table of N lines sharing an item costs a single request.
 *
 * Fails OPEN: a failed fetch, an unknown base UOM, or rules that haven't
 * arrived yet all leave that item unrestricted — the backend's to_base() 422
 * stays the hard guarantee, this is the early guard rail on top of it.
 */
export default function useItemAllowedUoms({
  baseUomByItem = {},
  uomOptions = [],
  enabled = true,
}) {
  const [rulesByItem, setRulesByItem] = useState({});
  const requestedRef = useRef(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Primitive key: the editor re-renders on every keystroke, and depending on
  // the object itself would re-fire this effect each time.
  const idsKey = useMemo(
    () => Object.keys(baseUomByItem).filter(id => baseUomByItem[id]).sort().join(','),
    [baseUomByItem],
  );

  useEffect(() => {
    if (!enabled || !idsKey) return;
    idsKey.split(',').forEach((itemId) => {
      if (requestedRef.current.has(itemId)) return;
      requestedRef.current.add(itemId);
      getItemUomConversions(itemId)
        .then((raw) => {
          if (mountedRef.current) {
            setRulesByItem(prev => ({ ...prev, [itemId]: Array.isArray(raw) ? raw : [] }));
          }
        })
        .catch(() => {
          // null = "unknown", which optionsFor() treats as unrestricted.
          if (mountedRef.current) setRulesByItem(prev => ({ ...prev, [itemId]: null }));
        });
    });
  }, [idsKey, enabled]);

  const allowedByItem = useMemo(() => {
    const out = {};
    if (!enabled || !uomOptions.length) return out;

    Object.entries(rulesByItem).forEach(([itemId, raw]) => {
      const baseUomId = baseUomByItem[itemId];
      if (raw === null || !baseUomId) return;
      const baseCode = resolveUomCodeById(baseUomId, uomOptions);
      if (!baseCode) return;

      const graph = buildConversionGraph(normalizeUomConversionList(raw, uomOptions));
      const allowed = new Set([String(baseUomId)]);
      uomOptions.forEach((opt) => {
        const code = resolveUomCodeById(opt.value, uomOptions);
        if (code && resolveConversion(graph, code, baseCode) != null) {
          allowed.add(String(opt.value));
        }
      });
      out[itemId] = allowed;
    });
    return out;
  }, [rulesByItem, uomOptions, baseUomByItem, enabled]);

  /** Options for one line. `currentUomId` is always kept selectable — a value
   *  vanishing out of a select reads as silent data loss — but is labelled
   *  when it has no path to base, the same way ItemForm flags an inactive UOM. */
  const optionsFor = useCallback((itemId, currentUomId) => {
    const allowed = itemId ? allowedByItem[itemId] : null;
    if (!allowed) return uomOptions;

    const filtered = uomOptions.filter(opt => allowed.has(String(opt.value)));
    if (currentUomId && !allowed.has(String(currentUomId))) {
      const current = uomOptions.find(opt => String(opt.value) === String(currentUomId));
      if (current) {
        return [...filtered, { ...current, label: `${current.label}${UNRESOLVED_SUFFIX}` }];
      }
    }
    return filtered;
  }, [allowedByItem, uomOptions]);

  const isRestricted = useCallback(
    itemId => Boolean(itemId && allowedByItem[itemId]),
    [allowedByItem],
  );

  /** Re-read one item's rules after they were edited elsewhere in the page.
   *
   *  The fetch effect above is deliberately one-shot per item id, so a rule
   *  added from a GRN line would otherwise not widen that line's UOM picker
   *  until the whole screen was reloaded. */
  const refreshItem = useCallback((itemId) => {
    if (!itemId) return;
    getItemUomConversions(itemId)
      .then((raw) => {
        if (mountedRef.current) {
          setRulesByItem(prev => ({ ...prev, [itemId]: Array.isArray(raw) ? raw : [] }));
        }
      })
      .catch(() => {
        if (mountedRef.current) setRulesByItem(prev => ({ ...prev, [itemId]: null }));
      });
  }, []);

  /** How many BASE units one `uomId` is, per the item master's own rules.
   *
   *  Only used to show the storekeeper what the master says while they type a
   *  per-GRN pack size over it — "item default 1 STRIP = 12 TAB, this GRN
   *  posts at 1:10". Null (rules not loaded, no path, unknown base) simply
   *  omits the hint; it never blocks anything. */
  const factorFor = useCallback((itemId, uomId) => {
    const raw = itemId ? rulesByItem[itemId] : null;
    const baseUomId = itemId ? baseUomByItem[itemId] : null;
    if (!raw || !baseUomId || !uomId) return null;
    if (String(uomId) === String(baseUomId)) return 1;
    const baseCode = resolveUomCodeById(baseUomId, uomOptions);
    const fromCode = resolveUomCodeById(uomId, uomOptions);
    if (!baseCode || !fromCode) return null;
    const graph = buildConversionGraph(normalizeUomConversionList(raw, uomOptions));
    return resolveConversion(graph, fromCode, baseCode);
  }, [rulesByItem, baseUomByItem, uomOptions]);

  return { optionsFor, isRestricted, factorFor, refreshItem };
}
